import { Command } from "commander";
import chalk from "chalk";
import { planSyncTasks, executeSyncTasks, PlannedSyncTask, SyncTaskResult } from "../sync/syncEngine.js";
import { resolvePackageManager } from "../packageManager/packageManagerService.js";
import { detectProjectType } from "../project/projectDetector.js";
import { promptSelect } from "../utils/prompt.js";
import { findPackageJson } from "../utils/files.js";
import fs from "fs";
import path from "path";
import { generateDiagnosticBundle } from "../diagnostics/bundleGenerator.js";

export const syncCommand = new Command("sync")
  .description("Synchronize React Native project dependencies and native configurations")
  .argument("[platform]", "Target platform to synchronize ('ios', 'android', or 'all')", "all")
  .option("--dry-run", "Show planned synchronization tasks without executing them")
  .option("--json", "Output results in JSON format")
  .option("--verbose", "Show detailed execution stdout/stderr and command logs")
  .action(async (platformArg: string, options: { dryRun?: boolean; json?: boolean; verbose?: boolean }) => {
    try {
      const cwd = process.cwd();
      const platform = (platformArg === "ios" || platformArg === "android") ? platformArg : "all";

      const packageJsonPath = findPackageJson(cwd);
      if (!packageJsonPath) {
        if (options.json) {
          console.log(JSON.stringify({ tasks: [] }, null, 2));
        } else {
          console.log();
          console.log(chalk.red("✖ No package.json found in current directory tree. Sync aborted."));
          console.log();
        }
        process.exit(1);
        return;
      }

      const root = path.dirname(packageJsonPath);
      const iosExists = fs.existsSync(path.join(root, "ios"));
      const androidExists = fs.existsSync(path.join(root, "android"));
      const projectType = detectProjectType(cwd);

      let needsPrebuild = false;
      if (platform === "ios" && !iosExists) needsPrebuild = true;
      if (platform === "android" && !androidExists) needsPrebuild = true;
      if (platform === "all" && (!iosExists || !androidExists)) needsPrebuild = true;

      // We do not run interactive prompting or prebuilding in dry-run mode
      if (needsPrebuild && !options.dryRun) {
        let accept = false;
        let question = "";
        if (projectType === "react-native-cli") {
          question = "Native project missing. Generate now?";
        } else if (projectType === "expo-managed") {
          question = "Expo Managed project detected. Native projects have not been generated. Generate native projects now using Expo Prebuild?";
        }

        if (question) {
          // If not a TTY or in non-interactive environment, skip prompting (accept defaults to false)
          if (process.stdout.isTTY) {
            const ans = await promptSelect(question, ["Yes", "No"]);
            if (ans === "Yes") {
              accept = true;
            }
          }
        }

        if (accept) {
          const { PrebuildEngine } = await import("../prebuild/prebuildEngine.js");
          const { ExpoPrebuildStrategy } = await import("../prebuild/expoPrebuildStrategy.js");
          const { ReactNativePrebuildStrategy } = await import("../prebuild/reactNativePrebuildStrategy.js");

          const prebuildEngine = new PrebuildEngine();
          prebuildEngine.registerStrategy(new ExpoPrebuildStrategy());
          prebuildEngine.registerStrategy(new ReactNativePrebuildStrategy());

          await prebuildEngine.runPrebuild(cwd, platform, {
            verbose: options.verbose,
            json: options.json,
          });
        }
      }

      const pmResolution = await resolvePackageManager(cwd, !!options.verbose);
      const pmName = pmResolution.selected;
      const planned = planSyncTasks(cwd, platform);

      if (planned.length === 0) {
        if (options.json) {
          console.log(JSON.stringify({ tasks: [] }, null, 2));
        } else {
          console.log();
          console.log(chalk.red("✖ No package.json found in current directory tree. Sync aborted."));
          console.log();
        }
        process.exit(1);
        return;
      }

      // 1. Dry run output
      if (options.dryRun) {
        if (options.json) {
          const list = planned.map((p) => ({ id: p.id, title: p.title }));
          console.log(JSON.stringify({ tasks: list }, null, 2));
        } else {
          console.log();
          console.log(chalk.bold.underline("Planned Tasks"));
          console.log();
          for (const task of planned) {
            console.log(`  ✓ ${task.title}`);
          }
          console.log();
          console.log(chalk.yellow("No actions executed. (Dry run)"));
          console.log();
        }
        process.exit(0);
        return;
      }

      if (!options.json) {
        console.log();
        console.log(chalk.bold.underline("React Native Sync"));
        console.log();
        console.log(`  ${chalk.green("✓")} Package Manager: ${chalk.bold(pmName)}`);
        console.log();
        console.log(chalk.bold("Running Tasks"));
      }

      const results = await executeSyncTasks(cwd, planned, platform, !!options.verbose, undefined, !!options.json);

      if (options.json) {
        const summary = results.map((r) => ({
          id: r.id,
          success: r.success,
          skipped: r.skipped,
          details: r.details,
        }));
        console.log(JSON.stringify({ tasks: summary }, null, 2));
      }

      const errorCount = results.filter((r) => !r.success && !r.skipped).length;
      if (errorCount > 0) {
        try {
          const reportPath = await generateDiagnosticBundle("sync", new Error(`Sync failed for ${errorCount} tasks.`));
          if (!options.json) {
            console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
          }
        } catch {}
      }

      process.exit(errorCount > 0 ? 1 : 0);
    } catch (err: any) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      let reportPath = "";
      try {
        reportPath = await generateDiagnosticBundle("sync", errorObj);
      } catch {}

      if (options.json) {
        console.log(JSON.stringify({ 
          error: errorObj.message,
          diagnosticBundle: reportPath 
        }, null, 2));
      } else {
        console.log();
        console.log(chalk.red(`✖ Sync failed: ${errorObj.message}`));
        if (reportPath) {
          console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
        }
        console.log();
      }
      process.exit(1);
    }
  });
